import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const triggers = [
  { value: 'BOOKING_CONFIRMATION', label: 'Booking Confirmation' },
  { value: 'PRE_ARRIVAL', label: 'Pre-Arrival (24h before)' },
  { value: 'CHECK_IN', label: 'Check-in Welcome' },
  { value: 'CHECK_OUT', label: 'Check-out Thank You' },
  { value: 'POST_STAY', label: 'Post-Stay Review Request' },
];

const Messaging = () => {
  const { selectedProperty } = useOutletContext();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    trigger: 'BOOKING_CONFIRMATION',
    subject: '',
    body: '',
    send_hours_before: 0,
    enabled: true
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/messaging/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/messaging`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message template created');
      setDialogOpen(false);
      setFormData({
        name: '',
        trigger: 'BOOKING_CONFIRMATION',
        subject: '',
        body: '',
        send_hours_before: 0,
        enabled: true
      });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create template');
    }
  };

  const handleToggle = async (templateId, currentEnabled) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/messaging/templates/${templateId}?enabled=${!currentEnabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/messaging/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="messaging-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Automated Messaging</h1>
          <p className="text-slate-600 mt-1">Configure email templates for guest communication</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-template-button">
              <Plus size={18} />
              New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Message Template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Welcome Email"
                  required
                />
              </div>

              <div>
                <Label>Trigger *</Label>
                <Select value={formData.trigger} onValueChange={(val) => setFormData({...formData, trigger: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {triggers.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Email Subject *</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="Welcome to {{property_name}}!"
                  required
                />
              </div>

              <div>
                <Label>Email Body *</Label>
                <Textarea
                  value={formData.body}
                  onChange={(e) => setFormData({...formData, body: e.target.value})}
                  placeholder="Dear {{guest_name}},\n\nThank you for booking with us!\n\nConfirmation: {{confirmation_number}}\nCheck-in: {{check_in}}\nCheck-out: {{check_out}}"
                  rows={8}
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  Available variables: {'{{guest_name}}'}, {'{{property_name}}'}, {'{{confirmation_number}}'}, {'{{check_in}}'}, {'{{check_out}}'}
                </p>
              </div>

              <Button type="submit" className="w-full">
                Create Template
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No message templates yet.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" />
              Create First Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => (
            <Card key={template.id} data-testid={`template-${template.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-cyan-600" />
                    <div>
                      <span>{template.name}</span>
                      <p className="text-sm font-normal text-slate-600">
                        {triggers.find(t => t.value === template.trigger)?.label}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={template.enabled}
                      onCheckedChange={() => handleToggle(template.id, template.enabled)}
                    />
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(template.id)}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-slate-600">Subject:</p>
                    <p className="font-medium">{template.subject}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Body Preview:</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-3">
                      {template.body}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Messaging;
